import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { quantileLabel } from '@/lib/quantiles';
import { IntervalCard } from '@/pages/aneel/IntervalCard';
import { aneelCaseParams, fetchAneelTimeline } from '@/services/aneel';
import type { AneelStageByBody } from '@/services/types';

/** The same floor the backend draws its ladders above. */
const LADDER_FLOOR = 20;

/**
 * How long an ANEEL case takes — and the chart this page refuses to draw.
 *
 * Outcomes by notice year is the most natural chart this data suggests, and it
 * would be almost entirely an artefact of case age: the 2024 cohort is 707 of
 * 1,569 notices with 16 decided. Drawn as a line it reads as the agency
 * collapsing. So the cohort table shows both counts and the trend is refused,
 * with the censoring stated in its own panel.
 *
 * The duration ladders carry a second disclosure. Seven cases record a decision
 * BEFORE the step it follows — one of them 260 days before its own notice. They
 * are left in the figures and counted separately, because a filtered ladder
 * would describe a tidier process than the records do.
 */
export function AneelTimelinePage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'timeline'],
    queryFn: fetchAneelTimeline,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:timeline.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:timeline.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:timeline.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/aneel/export/timeline" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:timeline.intervals.title')}
        </h2>
        <div className="space-y-3">
          {data.intervals.map((step) => (
            <IntervalCard key={step.stage} step={step} labelKey="aneel:timeline.intervals" />
          ))}
        </div>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:timeline.guide.intervals" />
        </div>
      </section>

      {/* The same steps by who lavrou the auto. Descriptive and alphabetical:
          whose desk the case sat on, never a comparison of bodies as
          adjudicators, which is the standing refusal on the overview. */}
      <section className="rounded-lg border bg-card p-4" data-by-body>
        <h2 className="mb-1 text-sm font-semibold text-foreground">
          {t('aneel:timeline.byBody.title')}
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('aneel:timeline.byBody.help')}</p>
        <div className="space-y-3">
          {data.by_body.map((stage) => (
            <StageByBodyCard key={stage.stage} stage={stage} />
          ))}
        </div>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:timeline.guide.byBody" />
        </div>
      </section>

      {/* The refusal gets a panel, not a footnote. It is the deliverable: the
          chart a reader came for is the one this corpus cannot support. The
          counts it withholds a LINE for sit in the table below, and it says so. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:timeline.trend.title')}
        </h2>
        <Answer
          answer={data.outcome_trend}
          help={t('aneel:timeline.trend.help')}
          elsewhere={t('aneel:timeline.trend.elsewhere')}
        >
          {() => null}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:timeline.cohorts.title')}
        </h2>
        <Answer answer={data.cohorts} help={t('aneel:timeline.cohorts.help')}>
          {(maturity) => (
            <div className="space-y-3">
              {/* The largest cohort, computed per request and until now shipped
                  without being rendered: the year a reader would take for a
                  surge in enforcement, named as a change in what was charged. */}
              {maturity.largest_year ? (
                <p
                  className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
                  data-largest-cohort={maturity.largest_year}
                >
                  {t('aneel:timeline.cohorts.largest', {
                    year: maturity.largest_year,
                    cases: number.format(maturity.largest_cases),
                    total: number.format(
                      maturity.cohorts.reduce((sum, row) => sum + row.cases, 0),
                    ),
                    decided: number.format(maturity.largest_decided),
                  })}
                </p>
              ) : null}
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('aneel:timeline.cohorts.year')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:timeline.cohorts.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:timeline.cohorts.decided')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:timeline.cohorts.share')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {maturity.cohorts.map((row) => (
                    <tr key={row.year} className="border-t" data-cohort={row.year}>
                      <td className="px-3 py-2 tabular-nums text-foreground">{row.year}</td>
                      {/* The year's notices open the list filtered to that
                          year: the same `year` predicate the list applies. */}
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={`/aneel/cases?${aneelCaseParams({ year: Number(row.year) }).toString()}`}
                          className="text-primary underline-offset-2 hover:underline"
                          aria-label={t('aneel:timeline.cohorts.openYear', {
                            n: number.format(row.cases),
                            year: row.year,
                          })}
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.decided)}
                      </td>
                      {/* The share is shown per ROW and never across rows. A
                          reader can compare two years themselves; what they are
                          not handed is a line implying the comparison is
                          meaningful. */}
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {percent.format(row.decided_share)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:timeline.guide.cohorts" />
        </div>
      </section>
    </div>
  );
}

/**
 * One step's ladders per inspecting body, or the refusal when no body clears
 * the floor. Alphabetical by construction on the wire; rendered in that order.
 */
function StageByBodyCard({ stage }: { stage: AneelStageByBody }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 });

  return (
    <div className="rounded-md border bg-background p-3" data-stage-by-body={stage.stage}>
      <h3 className="mb-2 text-xs font-medium text-foreground">
        {t(`aneel:timeline.intervals.${stage.stage}`, stage.stage)}
      </h3>
      <Answer answer={stage.by_body} help={t('aneel:timeline.intervals.help')}>
        {(bodies) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 font-medium">{t('aneel:timeline.byBody.body')}</th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      {t('aneel:timeline.byBody.completed')}
                    </th>
                    {Object.keys(bodies.rows[0]?.summary.quantiles ?? {}).map((key) => (
                      <th key={key} className="px-2 py-1.5 text-right font-medium">
                        {quantileLabel(t, key)}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 text-right font-medium">
                      {t('aneel:timeline.byBody.impossible')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bodies.rows.map((row) => (
                    <tr key={row.body} className="border-t" data-body={row.body}>
                      <td className="px-2 py-1.5 text-foreground">{row.body}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-foreground">
                        {number.format(row.completed)}
                      </td>
                      {Object.values(row.summary.quantiles).map((value, index) => (
                        <td
                          // Quantile columns are positional and never reordered.
                          key={index}
                          className="px-2 py-1.5 text-right tabular-nums text-muted-foreground"
                        >
                          {number.format(value)} {t('aneel:timeline.intervals.days')}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                        {number.format(row.impossible)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {bodies.suppressed_bodies > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed-bodies>
                {t('aneel:timeline.byBody.suppressed', {
                  bodies: number.format(bodies.suppressed_bodies),
                  minimum: LADDER_FLOOR,
                  cases: number.format(bodies.suppressed_cases),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </div>
  );
}

